import React, { useCallback, useMemo, type ReactNode, type RefObject } from 'react';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { modalEntryPoint } from './modalEntryPoint.entrypoint.tsx';

type ModalEntryPointTriggerProps = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	projectId: string;
	projectKey: string;
	containerId: string | undefined;
};

const modalProps: ModalDialogProps = {
	width: 'large',
};

export const ModalEntryPointTrigger = ({
	children,
	projectId,
	projectKey,
	containerId,
}: ModalEntryPointTriggerProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();

	const onClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'closed', actionSubject: 'modal' }),
			'aiViewGeneration',
		);
	}, [createAnalyticsEvent]);

	const entryPointProps = useMemo(
		() => ({
			projectId,
			onClose,
			projectKey,
			containerId,
		}),
		[onClose, projectId, projectKey, containerId],
	);

	const entryPointParams = useMemo(() => ({ cloudId }), [cloudId]);

	if (!cloudId) {
		return null;
	}

	return (
		<ModalTrigger
			entryPoint={modalEntryPoint}
			modalProps={modalProps}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			preloadOnFocus
		>
			{children}
		</ModalTrigger>
	);
};
